import { useState } from "react";
import { useIntl } from "react-intl";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { ConfirmationModal } from "src/components/ConfirmationModal/ConfirmationModal";
import Trash from "src/design-system/icons/Trash.svg?react";
import styled from "styled-components";
import { Field } from "src/design-system/components/Field/Field";
import { Button } from "src/design-system/components/Button/Button";
import { Toggle } from "../../../components/Toggle/Toggle";
import {
  color,
  fontSize,
  fontWeight,
  lineHeight,
  spacing,
} from "../../../theme";
import { desktopLayout } from "../../../utils/hooks/useLayout";
import type { UserProfile } from "../../utils/user";
import { messages } from "./ManageProfile.messages";
import { useManageProfile } from "./useManageProfile";

type Props = {
  user: UserProfile;
};

export function ManageProfile({ user }: Props) {
  const {
    name,
    email,
    toggle,
    handleNameChange,
    handleEmailChange,
    handleOnChangeToggle,
    handleUpdate,
    disabled,
    handleDeleteProfile,
  } = useManageProfile(user);

  const intl = useIntl();

  return (
    <Container>
      <InputsContainer>
        <InputContainer>
          <Field
            id="manage-profile-name"
            name="manage-profile-name"
            type="text"
            fieldSize="large"
            label={intl.formatMessage(messages.name)}
            value={name}
            onChange={(value) => handleNameChange(value)}
            autoComplete="name"
            inputMode="text"
            isRequired
            maxLength={100}
          />
        </InputContainer>
        <InputContainer>
          <Field
            id="manage-profile-email"
            name="manage-profile-email"
            type="email"
            fieldSize="large"
            label={intl.formatMessage(messages.email)}
            value={email}
            onChange={(value) => handleEmailChange(value)}
            autoComplete="email"
            inputMode="email"
            isRequired
            isDisabled={disabled}
          />
        </InputContainer>
      </InputsContainer>
      <ToggleContainer>
        <Toggle
          label="email-marketing"
          selected={toggle}
          setSelected={handleOnChangeToggle}
        />
        <ToggleLabel htmlFor="email-marketing-toggle">
          {intl.formatMessage(messages.emailMarketingLabel)}
        </ToggleLabel>
      </ToggleContainer>
      <Button
        size="large"
        className="mb-2 ml-auto"
        onClick={handleUpdate}
        isDisabled={disabled}
      >
        {intl.formatMessage(messages.update)}
      </Button>
      <DeleteProfileButton
        email={email}
        onDeleteProfile={handleDeleteProfile}
      />
    </Container>
  );
}

type DeleteProfileButtonProps = {
  email: string;
  onDeleteProfile: () => void;
};
function DeleteProfileButton({
  email,
  onDeleteProfile,
}: DeleteProfileButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const intl = useIntl();

  return (
    <>
      <Button
        className="flex items-center"
        variant="subtle"
        onPress={() => {
          sendAnalyticsInteractionEvent({
            category: "UserProfile",
            action: "Click:DeleteProfile",
          });
          setShowModal(true);
        }}
        size="medium"
      >
        <Trash className="h-4 w-4 text-icon-primary" />
        <span>{intl.formatMessage(messages.deleteProfileButtonText)}</span>
      </Button>
      <ConfirmationModal
        showModal={showModal}
        onCloseModal={() => {
          sendAnalyticsInteractionEvent({
            category: "DeleteProfileConfirmatiom",
            action: "Cancel:DeleteProfile",
          });
          setShowModal(false);
        }}
        title={intl.formatMessage(messages.deleteProfileTitle)}
        description={intl.formatMessage(messages.deleteProfileDescription, {
          br: <LineBreak />,
        })}
        confirmButtonText={intl.formatMessage(
          messages.deleteProfileConfirmButtonText,
        )}
        cancelButtonText={intl.formatMessage(messages.cancel)}
        onConfirm={() => {
          sendAnalyticsInteractionEvent({
            category: "DeleteProfileConfirmatiom",
            action: "Confirm:DeleteProfile",
          });
          onDeleteProfile();
        }}
        question={{
          title: intl.formatMessage(messages.primaryEmail),
          correctAnswer: email,
        }}
        $height="512px"
      />
    </>
  );
}

const Container = styled.form`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
`;
const InputsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: ${spacing.xxxl};
  margin-bottom: 40px;
`;
const InputContainer = styled.div`
  width: 100%;
`;

const ToggleContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 56px;
  ${desktopLayout} {
    margin-bottom: ${spacing.xxl};
  }
  span {
    flex-shrink: 0;
  }
`;
const ToggleLabel = styled.label`
  font-weight: ${fontWeight.normal};
  font-size: ${fontSize.body};
  line-height: ${lineHeight.normal};
  margin-left: ${spacing.xxl};

  color: ${color.black};
`;

// The design includes a line break in the middle of the description
// so we need to use a styled component to render it
const LineBreak = styled.br`
  display: block;
  margin-bottom: ${spacing.xl};
  content: "";
`;
